import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, finalize } from 'rxjs/operators';
import { omit } from 'lodash';

import { untilDestroyed, UrlParamsService } from '@app/core';
import { slideDownUpTrigger } from '@app/core/animations';
import { AppService, DocumentService, EnterpriseService, ErrorHandlerService, NotyService, UserService } from '@app/services';
import { ContextEnum, DocumentStatusEnum, ListFormat, SlimDocument, User } from '@app/models';
import { LoaderService } from '@app/shared';
import { DocumentsListingService } from '../shared/documents-listing.service';

@Component({
  selector: 'app-documents-organization-list',
  templateUrl: './documents-organization-list.component.html',
  styleUrls: ['./documents-organization-list.component.scss'],
  animations: [slideDownUpTrigger],
  standalone: false
})
export class DocumentsOrganizationListComponent implements OnInit, OnDestroy {
  ListFormat = ListFormat;
  documents: SlimDocument[];
  currentUser: User;
  currentContext: ContextEnum;
  isLoading = false;
  isLastPage = false;
  pageNumber: number;
  searchQuery: any;
  mobileSearch = '';
  isSearchOpen = false;
  openTaxonomyMenu = false;
  readonly ContextEnum = ContextEnum;
  readonly DocumentStatusEnum = DocumentStatusEnum;

  constructor(
    public route: ActivatedRoute,
    public appService: AppService,
    public documentsListingService: DocumentsListingService,
    public urlParamsService: UrlParamsService,
    public enterpriseService: EnterpriseService,
    private userService: UserService,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private documentService: DocumentService,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private router: Router
  ) {}

  ngOnInit() {
    this.currentContext = ContextEnum[this.route.snapshot.data.context as keyof typeof ContextEnum] || ContextEnum.Organization;

    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        filter(user => !this.currentUser || this.currentUser.organization.id !== user.organization.id || this.currentUser.member.group.id !== user.member.group.id),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          this.currentUser = user;
          let permission = 'view_documents';
          permission = permission + (this.currentContext === ContextEnum.Group ? '_gr' : '_oz');

          if ((user as any).currentPermissions[permission]) {
            if (!(this.documents || [])[0]) {
              this.isSearchOpen = this.documentsListingService.isSearchParams(this.route.snapshot.queryParams);
              this.searchQuery = this.documentsListingService.searchQueryFromQueryParams(this.route.snapshot.queryParams);
            }

            this.loadDocuments({ search: this.searchQuery });
          } else {
            this.userService.permissionCheck((user as any).currentPermissions[permission]);
          }
        }
      });
  }

  ngOnDestroy() {}

  loadDocuments(options: { page?: number; search?: any } = {}, extraOptions: { showLoader?: boolean } = {}) {
    this.isLoading = true;

    this.pageNumber = options.page || 1;
    const showLoader = extraOptions?.showLoader;
    if (showLoader) {
      this.loaderService.show();
    }
    this.documentsListingService.pushSearchQueryToUrl(options.search);

    this.documentService
      .organizationDocuments(this.documentsSearchParams(options.search), this.pageNumber === 1 ? 'cache-and-network' : 'no-cache')
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: page => {
          this.documents = page ? (this.pageNumber === 1 ? [] : this.documents || []).concat(page.data) : null;
          this.isLastPage = !page || !page.has_more_pages;
          if (this.pageNumber === 1) {
            this.appService.addFirstPageCounts({ org: this.documents.length });
          }
        },
        error: error => {
          const handledError = this.errorHandlerService.handle(error);
          if (handledError?.message === 'without_permission') {
            this.router.navigate(['/'], { replaceUrl: true });
          }
        }
      });
  }

  sign(documents: SlimDocument[]) {
    const userDocuments = documents.filter(doc => this.documentsListingService.hasCurrentUser(doc));
    if (userDocuments.length === 0) {
      this.notyService.error(this.translateService.instant('notyService.cannotSignUnauthorized'));
    } else {
      this.documentsListingService.sign(userDocuments).subscribe({
        next: () => {
          if (userDocuments.filter(doc => (doc as any)._isSelected).length > 0) {
            this.notyService.info(this.translateService.instant('couldnt_sign_documents_selected'));
          }
        },
        error: error => this.errorHandlerService.handle(error)
      });
    }
  }

  delete(documents: SlimDocument[]) {
    this.documentsListingService.delete(this.documents, documents, { context: this.currentContext }).subscribe({
      error: error => this.errorHandlerService.handle(error)
    });
  }

  transfer(documents: SlimDocument[]) {
    this.documentsListingService.transfer(this.documents, documents, { context: this.currentContext }).subscribe({
      next: () => this.loadDocuments({ page: 1, search: this.searchQuery }, { showLoader: true }),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  openFoldersModal(options: { documents?: SlimDocument[] }) {
    if (!options.documents) return;
    this.documentsListingService.openFoldersModal(this.documents, options.documents, {}, ContextEnum.Organization).subscribe();
  }

  resendSignatures(documents: SlimDocument[]) {
    this.documentsListingService.resendSignatures(documents).subscribe({
      next: () => this.notyService.success(this.translateService.instant('notyService.documentsResentToSignatories')),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  toggleBlock(documents: SlimDocument[]) {
    this.documentsListingService.toggleBlock(documents).subscribe({
      error: error => this.errorHandlerService.handle(error)
    });
  }

  resendWebhook(document: SlimDocument) {
    this.documentService.resendWebhook({ documentId: document.id }).subscribe({
      next: () => this.notyService.success(this.translateService.instant('notyService.documentResendWebhook')),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  clearFilters() {
    this.appService.documentStatusFilter = null;
    this.mobileSearch = '';
    this.searchQuery = null;
    this.isSearchOpen = false;
    this.loadDocuments({ page: 1 });
  }

  toggleSearch(value: boolean) {
    this.isSearchOpen = value;
    if (this.isSearchOpen) {
      this.searchQuery = { ...(this.searchQuery || {}) };
    } else {
      this.clearFilters();
    }
  }

  exportDocuments(searchQuery: any) {
    if (!this.currentUser.email) {
      this.notyService.info(this.translateService.instant('cant_export_documents_selected_when_email_isnt_registered'));
      return;
    }

    this.isLoading = true;
    this.documentsListingService
      .exportDocuments(omit(this.documentsSearchParams(searchQuery), ['page', 'limit', 'sandbox']))
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        error: error => this.errorHandlerService.handle(error)
      });
  }

  routeToNewDocument() {
    this.router.navigate(['/documentos/novo'], { replaceUrl: true });
  }

  private documentsSearchParams(searchQuery: any) {
    return {
      status: this.appService.documentStatusFilter,
      limit: this.documentsListingService.perPage,
      page: this.pageNumber,
      context: (searchQuery || {}).groupUuid ? ContextEnum.Group : this.currentContext,
      sandbox: this.appService.showSandbox,
      orderBy: { field: this.appService.listSorting, direction: this.appService.listDirection },
      ...(Object.keys(searchQuery || {}).length > 0 ? { status: undefined, ...searchQuery } : {})
    };
  }
}
